<template>
  <div class="modal-overflow">
    <div class="row">
      <div class="col-md-12">
        <div class="form-group">
          <label>Name</label>
          <el-input type="text"
                 name="name"
                 placeholder="Name"
                 ref="name"
                 :disabled="!canEdit"
                 v-validate="modelValidations.name"
                 v-model="edit.name" />
          <small class="text-danger" v-show="errors.has('name')">
            {{ getError('name') }}
          </small>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <div class="form-group">
          <h6 style="padding-top:18px">Clients</h6>
          <div style="margin-top:18px;">
            <el-autocomplete
                style="width:100%"
                class="inline-input"
                clearable
                v-model="clientSearch"
                :debounce="150"
                ref="clientId"
                :fetch-suggestions="findClients"
                :trigger-on-focus="false"
                value-key="name"
                placeholder="Add Client"
                @select="addClient"
                @blur="verifyClientSelected"
                data-vv-name="clientId">
            </el-autocomplete>
          </div>
        </div>
        <div class="row client-builder-row" v-for="clientId in edit.clientIds" :key="clientId">
            <div class="col-md-10" style="padding-top: 15px;">
              <div class="grey-font">{{ getClientName(clientId) }}</div>
            </div>
            <div class="col-md-2">
              <p-button class="pull-right" type="danger" v-if="canEdit" size="sm" icon @click="deleteClient(clientId)">
                <i class="fa fa-times"></i>
              </p-button>
            </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <div class="form-group">
          <h6 style="padding-top:18px">Builders</h6>
          <div style="margin-top:18px;">
            <el-autocomplete
                style="width:100%"
                class="inline-input"
                clearable
                v-model="builderSearch"
                :debounce="150"
                ref="builderId"
                :fetch-suggestions="findBuilders"
                :trigger-on-focus="false"
                value-key="name"
                placeholder="Add Builder"
                @select="addBuilder"
                @blur="verifyBuilderSelected"
                data-vv-name="builderId">
            </el-autocomplete>
          </div>
        </div>
        <div class="row client-builder-row" v-for="builderId in edit.builderIds" :key="builderId">
          <div class="col-md-10" style="padding-top: 15px;">
            <div class="grey-font">{{ getBuilderName(builderId) }}</div>
          </div>
          <div class="col-md-2">
            <p-button class="pull-right" type="danger" v-if="canEdit" size="sm" icon @click="deleteBuilder(builderId)">
              <i class="fa fa-times"></i>
            </p-button>
          </div>
        </div>
      </div>
    </div>
    <div class="row" style="margin-top: 15px">
      <div class="col-md-8">
        <auditable-entity :data="edit"></auditable-entity>
      </div>
      <div class="col-md-4"  v-if="canEdit">
        <div class="form-group form-group-active-label">
          <label class="active-label">Active</label>
          <p-switch v-model="edit.active" type="primary" on-text="YES" off-text="NO"></p-switch>
        </div>
        <button
          type="button"
          :disabled="isSubmitDisabled"
          @click="validateAndSubmit"
          class="btn btn-fill btn-info btn-wd pull-right"> Save
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import {
    findCompany,
    notifySuccess,
    notifyWarning,
    notifyError,
    objectById,
    verifyAutocompleteSelectedAndListPopulated
  } from 'src/utilities/utils'
  import Constants from 'src/utilities/constants'
  import Permissions from 'src/utilities/permissions'
  import PButton from 'src/components/UIComponents/Button.vue'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import AuditableEntity from 'src/components/UIComponents/AuditableEntity'
  import {sync} from 'vuex-pathify'
  import {CREATED, OK, UNAUTHORIZED} from 'http-status-codes'

  export default {
    props: ['data'],
    components: {
      AuditableEntity,
      PButton,
      PSwitch
    },
    computed: {
      edit: sync('communities@edit'),
      ...mapGetters({
        clients: 'companies/results',
        builders: 'companies/results',
        autocompleteClients: 'companies/autocompleteResultsClient',
        autocompleteBuilders: 'companies/autocompleteResultsBuilder'
      }),
      canEdit() {
        return this.$can('edit', Permissions.COMMUNITIES)
      },
      isSubmitDisabled() {
        return this.isProcessing
      }
    },
    data() {
      return {
        constants: Constants,
        clientSearch: '',
        builderSearch: '',
        communitySaved: false,
        isProcessing: false,
        newCompany: {
          builder: false,
          client: false
        },
        modelValidations: {
          name: {
            required: true,
            max: 64
          }
        }
      }
    },
    methods: {
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      async validateAndSubmit() {
        this.isProcessing = true

        if (await this.$validator.validateAll()) {
          const payload = this.$store.get('communities/savePayload', 'communities')
          const response = await this.$store.dispatch('save2', payload)

          if (OK === response.status || CREATED === response.status) {
            this.$store.commit('communities/resetEdit')
            this.$eventHub.$emit('communitySaved', response.data)
            this.communitySaved = true
            this.$emit('close')
            notifySuccess(this, 'Community Saved Successfully')
          } else if (response.status === UNAUTHORIZED) {
            notifyWarning(this, 'You are not authorized to create or edit a Community')
          } else {
            notifyError(this, 'Error Saving Community', response.errors)
          }
        }
        this.isProcessing = false
      },
      async findCompany(text, type) {
        return findCompany(this, text, type)
      },
      setCompany(company) {
        if (this.newCompany.builder) {
          this.addBuilder(company)
        } else if (this.newCompany.client) {
          this.addClient(company)
        }
        this.resetLocalCompany()
      },
      resetLocalCompany(type) {
        if (type && type === Constants.COMPANY.TYPES.CLIENT) {
          this.clientSearch = ''
        }
        if (type && type === Constants.COMPANY.TYPES.BUILDER) {
          this.builderSearch = ''
        }
        this.newCompany.client = false
        this.newCompany.builder = false
      },
      findClients(text, cb) {
        this.findCompany(text, Constants.COMPANY.TYPES.CLIENT)
        cb(this.autocompleteClients)
      },
      addClient(client) {
        this.$store.commit('companies/appendToResults', client)
        this.$store.commit('communities/addClient', client.id)
        this.clientSearch = ''
      },
      deleteClient(clientId) {
        this.$store.commit('communities/deleteClient', clientId)
      },
      getClientName(clientId) {
        const client = objectById(this.clients, clientId)
        if (client) {
          return client.name
        }
        return clientId
      },
      findBuilders(text, cb) {
        this.findCompany(text, Constants.COMPANY.TYPES.BUILDER)
        cb(this.autocompleteBuilders)
      },
      addBuilder(builder) {
        this.$store.commit('companies/appendToResults', builder)
        this.$store.commit('communities/addBuilder', builder.id)
        this.builderSearch = ''
      },
      deleteBuilder(builderId) {
        this.$store.commit('communities/deleteBuilder', builderId)
      },
      verifyClientSelected() {
        if (this.newCompany.client) {
          return
        }
        verifyAutocompleteSelectedAndListPopulated(
            this,
            'communities/edit',
            'client',
            'clientSearch',
            'clientIds')
      },
      verifyBuilderSelected() {
        if (this.newCompany.builder) {
          return
        }
        verifyAutocompleteSelectedAndListPopulated(
            this,
            'communities/edit',
            'builder',
            'builderSearch',
            'builderIds')
      },
      getBuilderName(builderId) {
        const builder = objectById(this.builders, builderId)
        if (builder) {
          return builder.name
        }
        return builderId
      }
    },
    async beforeMount() {
      this.$store.commit('communities/resetEdit')
      if (this.data) {
        // editing existing community
        if (this.data.id) {
          const response = await this.$store.dispatch('fetchById', {
            endpoint: Constants.ENTITY_ENDPOINTS.COMMUNITIES,
            id: this.data.id
          })

          if (OK !== response.status) {
            notifyError(this, 'Failed to load Community')
            return
          }

          const allIds = [].concat(response.data.builderIds).concat(response.data.clientIds)

          const companiesResponse = await this.$store.dispatch('fetchByIds', {
            endpoint: Constants.ENTITY_ENDPOINTS.COMPANIES,
            ids: allIds
          })

          if (OK === companiesResponse.status) {
            this.$store.commit("companies/results", companiesResponse.data)
          } else {
            notifyWarning(this, "Failed to load company data")
          }

          this.$store.commit('communities/hydrateEdit', response.data)
        } else {
          // creating new community on the fly
          this.edit.name = this.data.name
          this.edit.active = this.data.active
          if (this.data.client) {
            this.addClient(this.data.client)
          }
          if (this.data.builder) {
            this.addBuilder(this.data.builder)
          }
        }
      }
    },
    mounted() {
      this.$eventHub.$on('companySaved', this.setCompany)
      this.$eventHub.$on('noCompanySelected', this.resetLocalCompany)
    },
    destroyed() {
      this.$store.commit('communities/resetEdit')
      this.$eventHub.$off('companySaved')
      this.$eventHub.$off('noCompanySelected')
      if (!this.communitySaved) {
        this.$eventHub.$emit('noCommunitySelected')
      }
    }
  }
</script>

<style scoped>
  label {
    display: block;
  }
  .client-builder-row {
    border: 1px solid #ddd;
    padding: 0;
    margin: 0 0 5px 0;
    border-radius: 7px;
  }
</style>
